import React, {
  useCallback,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { Draggable } from "gsap/Draggable";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { InertiaPlugin } from "gsap/InertiaPlugin";

import { Book } from "@/utils/bookRouting";

gsap.registerPlugin(ScrollTrigger, Draggable, ScrollToPlugin, InertiaPlugin);

interface BookShowcaseSectionProps {
  books: Book[];
  isDarkMode: boolean;
  onBookSelect: (bookSlug: string) => void;
  maxBooksToShow?: number;
  onNavigateToExplore?: () => void;
}

// Card sizing constant
const CARD_ASPECT_RATIO = 280 / 420;

export interface BookShowcaseSectionHandle {
  container: HTMLDivElement | null;
  header: HTMLHeadingElement | null;
  paragraph: HTMLParagraphElement | null;
}

const BookShowcaseSection = forwardRef<
  BookShowcaseSectionHandle,
  BookShowcaseSectionProps
>(
  (
    {
      books,
      isDarkMode,
      onBookSelect,
      maxBooksToShow = 10,
      onNavigateToExplore,
    },
    ref
  ) => {
    const displayedBooks = useMemo(
      () => books.slice(0, maxBooksToShow),
      [books, maxBooksToShow]
    );

    const containerRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLHeadingElement>(null);
    const paragraphRef = useRef<HTMLParagraphElement>(null);
    const carouselRef = useRef<HTMLDivElement>(null);
    const dragProxyRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      container: containerRef.current,
      header: headerRef.current,
      paragraph: paragraphRef.current,
    }));

  // Tracks the active snap animation so it can be killed instantly on user input
  const snapTween = useRef<gsap.core.Tween | null>(null);

  const handleBookClick = useCallback(
    (book: Book) => {
      onBookSelect(book.slug);
    },
    [onBookSelect]
  );


  // Main Carousel
  useGSAP(
    () => {
      const containerEl = containerRef.current;
      const carouselEl = carouselRef.current;
      const dragProxyEl = dragProxyRef.current;

      if (!carouselEl || !containerEl || !dragProxyEl) return;

      // Scope card selection to this component instance
      const cards = gsap.utils.toArray<HTMLElement>(".carousel-book-card", carouselEl);
      if (!cards.length) return;

      // Touch detection
      const isTouch = ScrollTrigger.isTouch > 0;


      // Carousel constants
      const spacing = 0.108;
      const minTime = 0.5;
      const maxTime = minTime + (cards.length - 1) * spacing;
      const totalDuration = maxTime - minTime;

      gsap.set(cards, { xPercent: 400, opacity: 0, scale: 0 });

      const playhead = { offset: minTime };

      // Render Logic
      function renderGapless() {
        const playheadIndex = (playhead.offset - minTime) / spacing;

        cards.forEach((card, i) => {
          const indexOffset = i - playheadIndex;
          const absOffset = Math.abs(indexOffset);

          const xPercent =
            indexOffset * 95 + (indexOffset / (absOffset + 1)) * 10;

          const dist = absOffset;
          const visible = dist <= 2.2;
          const t = gsap.utils.clamp(0, 1, 1 - dist / 2.2);

          gsap.set(card, {
            xPercent: xPercent - 50,
            opacity: visible ? t : 0,
            scale: visible ? 0.75 + 0.25 * t : 0,
            zIndex: Math.round(1000 - dist * 10),
          });
        });
      }

      // Animation Tween (used only for NON-touch scrolling)
      const animation = gsap.to(playhead, {
        offset: maxTime,
        duration: 1,
        ease: "none",
        paused: true,
        onUpdate: renderGapless,
      });

      // ScrollTrigger
      const trigger = ScrollTrigger.create(
        isTouch
          ? {
              trigger: containerEl,
              start: "top top",
              pin: false,
            }
          : {
              trigger: containerEl,
              start: "top top",
              end: `+=${cards.length * 400}`,
              pin: true,
              scrub: 0.5,
              animation,
            }
      );

      // Initial Render
      renderGapless();

      // Interaction Handling
      const killSnap = () => {
        snapTween.current?.kill();
        snapTween.current = null;
      };

      // Manual Snapping Logic (only relevant when scroll drives progress)
      const snapToBook = () => {
        if (isTouch) return;

        const progress = trigger.progress;
        if (progress <= 0.05 || progress >= 0.95) return;

        const rawIndex = (progress * totalDuration) / spacing;

        const clampedIndex = gsap.utils.clamp(
          0,
          cards.length - 1,
          Math.round(rawIndex)
        );

        const targetProgress = (clampedIndex * spacing) / totalDuration;
        const targetScroll =
          trigger.start + targetProgress * (trigger.end - trigger.start);

        snapTween.current = gsap.to(window, {
          scrollTo: targetScroll,
          duration: 0.5,
          ease: "power2.out",
        });
      };

      // Only bind scroll-related listeners on NON-touch
      if (!isTouch) {
        window.addEventListener("wheel", killSnap, { passive: true });
        window.addEventListener("touchmove", killSnap, { passive: true });
        window.addEventListener("pointerdown", killSnap, { passive: true });
        ScrollTrigger.addEventListener("scrollEnd", snapToBook);
      }

      // Track touch swipe velocity manually (px/sec)
      let lastDragX = 0;
      let lastDragTime = 0;
      let velocityX = 0;

      type DragState = Draggable & { startScroll: number; startOffset: number };

      const [draggable] = Draggable.create(dragProxyEl, {
        type: "x",
        trigger: carouselEl,
        // inertia: isTouch,
        throwResistance: isTouch ? 1200 : undefined, // Calms throw a bit on touch

        onPress() {
          const self = this as DragState;

          killSnap();
          self.startScroll = trigger.scroll();
          self.startOffset = playhead.offset;

          // Init velocity tracking
          lastDragX = self.x;
          lastDragTime = performance.now();
          velocityX = 0;
        },

        onDrag() {
          const self = this as DragState;

          // Velocity tracking
          const nowX = self.x;
          const nowT = performance.now();
          const dt = nowT - lastDragTime;

          if (dt > 0) {
            const dx = nowX - lastDragX;
            velocityX = (dx / dt) * 1000; // px/sec
            lastDragX = nowX;
            lastDragTime = nowT;
          }

          const deltaX = self.startX - self.x;

          if (isTouch) {
            // Sensitivity tuned for finger travel (px -> offset)
            const sensitivity = 0.001;

            gsap.to(playhead, {
              offset: gsap.utils.clamp(
                minTime,
                maxTime,
                self.startOffset + deltaX * sensitivity
              ),
              duration: 0.25,
              ease: "power2.out",
              overwrite: true,
              onUpdate: renderGapless,
            });

            return;
          }

          // Desktop: drag drives scroll
          const target = self.startScroll + deltaX * 2.5;
          trigger.scroll(gsap.utils.clamp(trigger.start, trigger.end, target));
        },

        onThrowUpdate() {
          if (!isTouch) return;

          const self = this as DragState;

          // Update velocity tracking during inertia too (helps flick detection)
          const nowX = self.x;
          const nowT = performance.now();
          const dt = nowT - lastDragTime;

          if (dt > 0) {
            const dx = nowX - lastDragX;
            velocityX = (dx / dt) * 1000; // px/sec
            lastDragX = nowX;
            lastDragTime = nowT;
          }

          const deltaX = self.startX - self.x;
          const sensitivity = 0.0025;

          playhead.offset = gsap.utils.clamp(
            minTime,
            maxTime,
            self.startOffset + deltaX * sensitivity
          );

          renderGapless();
        },

        onDragEnd() {
          if (!isTouch) {
            snapToBook();
            return;
          }

          // Touch snap, velocity-aware
          const rawIndex = (playhead.offset - minTime) / spacing;

          // Bias snap in flick direction when it's a real flick
          const flickThreshold = 100; // px/sec
          const flickBias =
            Math.abs(velocityX) > flickThreshold
              ? velocityX > 0
                ? -0.55
                : 0.55
              : 0;

          const snappedIndex = gsap.utils.clamp(
            0,
            cards.length - 1,
            Math.round(rawIndex + flickBias)
          );

          const snappedOffset = minTime + snappedIndex * spacing;

          gsap.to(playhead, {
            offset: snappedOffset,
            duration: 0.45, // Snap duration
            ease: "power2.out",
            onUpdate: renderGapless,
          });
        },
      });

      // Cleanup
      return () => {
        killSnap();
        trigger.kill();
        animation.kill();
        draggable?.kill();

        if (!isTouch) {
          ScrollTrigger.removeEventListener("scrollEnd", snapToBook);
          window.removeEventListener("wheel", killSnap);
          window.removeEventListener("touchmove", killSnap);
          window.removeEventListener("pointerdown", killSnap);
        }
      };
    },
    // Re-init when the displayed book set changes (not just its length).
    // revertOnUpdate kills the previous pin first; without it each re-run
    // stacks another pin on the same element.
    { scope: containerRef, dependencies: [displayedBooks], revertOnUpdate: true }
  );

  return (
    <section
      id="book-showcase"
      ref={containerRef}
      className="w-full h-dvh overflow-hidden pt-30 xs:pt-38 sm:pt-42 px-8 lg:px-16 xl:px-24 flex flex-col justify-start relative max-h-screen"
    >
      <div className="text-left mb-12 sm:mb-24 relative z-10">
        <h2
          ref={headerRef}
          className={`main-title portrait-text-app text-3xl 2xs:text-4xl xs:text-5xl lg:text-6xl font-bold mb-8 lg:mb-9 break-normal ${
            isDarkMode ? "text-theme-primary-dark" : "text-blue-700"
          }`}
        >
          An expanding library{" "}
          <span className="whitespace-nowrap">of great literature</span>
        </h2>
        <p
          ref={paragraphRef}
          className={`
          text-lg xs:text-xl md:text-2xl lg:text-2xl
          ${isDarkMode ? "text-theme-secondary-dark" : "text-blue-700"}
        `}
        >
          A growing list of great books, curated by legendary literary editor
          Barbara Epler.
        </p>
      </div>

      {/* Remaining space split: 2/3 carousel, 1/3 button */}
      <div className="flex flex-col flex-1 min-h-0">
        {/* Carousel */}
        <div ref={carouselRef} className="relative flex-[2.5] min-h-0 w-full">
          {/* Horizontal clip WITHOUT using overflow (so vertical shadows can render) */}
          <div
            className="relative w-full h-full max-h-full overflow-visible"
            style={{ clipPath: "inset(-1000px 0 -1000px 0)" }}
          >
            <div ref={dragProxyRef} className="drag-proxy absolute inset-0 z-50" />

            {displayedBooks.map((book) => (
              <div
                key={book.id}
                className="carousel-book-card absolute left-1/2"
                role="button"
                tabIndex={0}
                onClick={() => handleBookClick(book)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleBookClick(book);
                  }
                }}
                style={{
                  cursor: "pointer",
                }}
              >
                <img
                  src={book.coverUrl}
                  alt={book.title}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full shadow-2xl"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Explore Button */}
        <div className="flex-1 min-h-0 flex items-center justify-center relative z-20">
          <div className="text-center">
            <button
              type="button"
              onClick={onNavigateToExplore}
              className={`px-5 py-3 rounded-full text-base transition-all shadow-sm select-none cursor-pointer hover:shadow-md hover:scale-102 border ${
                isDarkMode
                  ? "border-blue-600 text-white bg-blue-600 hover:bg-blue-700"
                  : "border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white"
              }`}
            >
              Explore curated collections
            </button>
          </div>
        </div>
      </div>
    </section>
  );
});

export default BookShowcaseSection;
